/**
 * Shared helpers for the Shop page and its sub-components (ProductCard,
 * ProductModal). Kept in a tiny file so they can be imported without pulling
 * in the whole Shop orchestrator.
 */

/** Format a price range using Intl.NumberFormat. `to == null` (or ~equal to
 * `from`) renders a single price; otherwise renders "$FROM – $TO". */
export function formatPrice(from, to, currency = "USD") {
  if (from == null) return "—";
  const fmt = (n) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(n);
  if (to == null || Math.abs(to - from) < 0.01) return fmt(from);
  return `${fmt(from)} – ${fmt(to)}`;
}

// Best-effort color-name -> CSS hex mapping for the shop-card swatch dots.
// Falls back to a checkered gradient when we can't identify the name.
export const COLOR_MAP = {
  black: "#0A0A0A", white: "#FFFFFF", navy: "#0E1E3A", "navy blue": "#0E1E3A",
  red: "#C0192E", "cardinal red": "#C0192E", pink: "#B32027", "hot pink": "#B32027",
  blue: "#1F5FBF", "royal blue": "#1F5FBF", green: "#1E7A3A", "kelly green": "#1E7A3A",
  yellow: "#F1C232", gold: "#C9A227", grey: "#7F7F7F", gray: "#7F7F7F",
  "heather grey": "#9E9E9E", "sport grey": "#B0B0B0", "athletic heather": "#B0B0B0",
  charcoal: "#3A3A3A", brown: "#5A3A2A", tan: "#D2B48C", cream: "#F5E6D3",
  "off-white": "#F1F1F1", ash: "#C9C9C9", lilac: "#C5A3D9", lavender: "#C5A3D9",
  purple: "#5E2CA5", orange: "#E85A1A", teal: "#2A8C7E", mint: "#9CE0B8",
  maroon: "#5C2028", cobalt: "#0F4CA6", olive: "#6F7B3B", camo: "#4E5B32",
};

/** Return a CSS `style` object for a color swatch. Falls back to a diagonal
 * gradient for unknown color names. */
export function swatchStyle(colorName) {
  const key = (colorName || "").trim().toLowerCase();
  if (COLOR_MAP[key]) return { background: COLOR_MAP[key] };
  // fuzzy substring match — "Sport Grey" -> "grey" etc.
  for (const k of Object.keys(COLOR_MAP)) if (key.includes(k)) return { background: COLOR_MAP[k] };
  return { background: "linear-gradient(135deg, #666 0 50%, #ccc 50% 100%)" };
}
